// components/shared/CourseCardSkeleton.tsx
import type { ReactElement } from 'react';

export type CourseCardSkeletonVariant =
  | 'ultra-compact'
  | 'compact'
  | 'expanded'
  | 'search-result'
  | 'recommendation';

export interface CourseCardSkeletonProps {
  variant?: CourseCardSkeletonVariant;
  count?: number;
}

const CourseCardSkeleton = ({ variant = 'compact', count = 1 }: CourseCardSkeletonProps) => {
  const pulseAnimation = 'animate-pulse bg-gray-200 rounded';

  const renderSkeleton = (index: number): ReactElement | null => {
    switch (variant) {
      case 'ultra-compact':
        return (
          <div
            key={index}
            className="p-2 bg-white border border-gray-200 rounded-lg animate-fade-in"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 flex-1">
                <div className={`${pulseAnimation} w-4 h-4`}></div>
                <div className={`${pulseAnimation} h-4 w-16`}></div>
                <div className={`${pulseAnimation} h-4 w-32 flex-1 max-w-xs`}></div>
                <div className={`${pulseAnimation} h-4 w-12`}></div>
              </div>
              <div className="flex gap-1">
                <div className={`${pulseAnimation} w-7 h-7 rounded`}></div>
                <div className={`${pulseAnimation} w-7 h-7 rounded`}></div>
              </div>
            </div>
          </div>
        );

      case 'compact':
        return (
          <div
            key={index}
            className="p-3 bg-white border border-gray-200 rounded-lg animate-fade-in"
          >
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-1">
                  <div className={`${pulseAnimation} w-4 h-4`}></div>
                  <div className={`${pulseAnimation} h-5 w-20`}></div>
                  <div className={`${pulseAnimation} h-5 w-40 flex-1 max-w-sm`}></div>
                  <div className={`${pulseAnimation} h-4 w-12`}></div>
                </div>
                <div className="flex gap-1">
                  <div className={`${pulseAnimation} w-8 h-8 rounded`}></div>
                  <div className={`${pulseAnimation} w-8 h-8 rounded`}></div>
                </div>
              </div>

              <div className="flex gap-1">
                <div className={`${pulseAnimation} h-5 w-16 rounded-full`}></div>
                <div className={`${pulseAnimation} h-5 w-20 rounded-full`}></div>
              </div>

              {Math.random() > 0.5 && <div className={`${pulseAnimation} h-3 w-3/4`}></div>}
            </div>
          </div>
        );

      case 'expanded':
        return (
          <div
            key={index}
            className="p-4 bg-white border border-gray-200 rounded-lg animate-fade-in"
          >
            <div className="space-y-3">
              <div className="flex items-start justify-between">
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className={`${pulseAnimation} h-6 w-24`}></div>
                    <div className={`${pulseAnimation} h-6 w-48`}></div>
                  </div>
                  <div className={`${pulseAnimation} h-4 w-16`}></div>
                </div>
                <div className="flex gap-1">
                  <div className={`${pulseAnimation} w-8 h-8 rounded`}></div>
                  <div className={`${pulseAnimation} w-8 h-8 rounded`}></div>
                  <div className={`${pulseAnimation} w-8 h-8 rounded`}></div>
                </div>
              </div>

              <div className="space-y-1">
                <div className={`${pulseAnimation} h-3 w-full`}></div>
                <div className={`${pulseAnimation} h-3 w-5/6`}></div>
                <div className={`${pulseAnimation} h-3 w-4/6`}></div>
              </div>

              <div className="flex gap-2">
                <div className={`${pulseAnimation} h-5 w-20 rounded-full`}></div>
                <div className={`${pulseAnimation} h-5 w-24 rounded-full`}></div>
                <div className={`${pulseAnimation} h-5 w-16 rounded-full`}></div>
              </div>
            </div>
          </div>
        );

      case 'search-result':
        return (
          <div
            key={index}
            className="p-3 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow animate-fade-in"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <div className={`${pulseAnimation} w-4 h-4`}></div>
                  <div className={`${pulseAnimation} h-4 w-20`}></div>
                  <div className={`${pulseAnimation} h-4 w-48`}></div>
                  <div className={`${pulseAnimation} h-4 w-12`}></div>
                </div>

                <div className="flex gap-1">
                  <div className={`${pulseAnimation} h-5 w-12 rounded`}></div>
                  <div className={`${pulseAnimation} h-5 w-12 rounded`}></div>
                  <div className={`${pulseAnimation} h-5 w-24 rounded`}></div>
                </div>

                <div className={`${pulseAnimation} h-3 w-64`}></div>
              </div>

              <div className="flex items-center gap-1">
                <div className={`${pulseAnimation} w-6 h-6 rounded`}></div>
                <div className={`${pulseAnimation} w-6 h-6 rounded`}></div>
              </div>
            </div>
          </div>
        );

      case 'recommendation':
        return (
          <div
            key={index}
            className="p-3 bg-gray-50 border border-gray-200 rounded-lg animate-fade-in"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <div className={`${pulseAnimation} h-4 w-8`}></div>
                  <div className={`${pulseAnimation} h-5 w-20`}></div>
                  <div className={`${pulseAnimation} h-5 w-40`}></div>
                  <div className={`${pulseAnimation} h-4 w-12`}></div>
                </div>

                <div className={`${pulseAnimation} h-4 w-48`}></div>

                <div className="space-y-1">
                  <div className="flex gap-1">
                    <div className={`${pulseAnimation} w-1 h-1 rounded-full`}></div>
                    <div className={`${pulseAnimation} h-3 w-64`}></div>
                  </div>
                  <div className="flex gap-1">
                    <div className={`${pulseAnimation} w-1 h-1 rounded-full`}></div>
                    <div className={`${pulseAnimation} h-3 w-56`}></div>
                  </div>
                </div>

                <div className="flex gap-2 pt-1">
                  <div className={`${pulseAnimation} h-7 w-20 rounded`}></div>
                  <div className={`${pulseAnimation} h-7 w-20 rounded`}></div>
                </div>
              </div>

              <div className="ml-3 flex flex-col items-center gap-1">
                <div className={`${pulseAnimation} w-12 h-12 rounded-full`}></div>
                <div className={`${pulseAnimation} h-3 w-8`}></div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className={index > 0 ? 'mt-2' : ''}>
          {renderSkeleton(index)}
        </div>
      ))}
    </>
  );
};

export interface CourseListSkeletonProps {
  variant?: CourseCardSkeletonVariant;
  count?: number;
  title?: string;
}

export const CourseListSkeleton = ({
  variant = 'compact',
  count = 3,
  title,
}: CourseListSkeletonProps) => {
  return (
    <div className="space-y-2 animate-fade-in">
      {title && (
        <div className="mb-3">
          <div className="animate-pulse bg-gray-200 h-5 w-32 rounded"></div>
        </div>
      )}
      <CourseCardSkeleton variant={variant} count={count} />
    </div>
  );
};

export interface SearchResultsSkeletonProps {
  count?: number;
}

export const SearchResultsSkeleton = ({ count = 5 }: SearchResultsSkeletonProps) => {
  return (
    <div className="space-y-3 animate-fade-in">
      <div className="flex items-center justify-between">
        <div className="animate-pulse bg-gray-200 h-4 w-24 rounded"></div>
        <div className="animate-pulse bg-gray-200 h-3 w-20 rounded"></div>
      </div>

      <div className="border border-gray-200 rounded-md p-2">
        <CourseCardSkeleton variant="search-result" count={count} />
      </div>
    </div>
  );
};

export const SemesterSkeleton = () => {
  return (
    <div className="border border-gray-200 rounded-lg animate-fade-in">
      <div className="p-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="animate-pulse bg-gray-200 h-5 w-20 rounded"></div>
            <div className="animate-pulse bg-gray-200 h-4 w-32 rounded"></div>
          </div>
          <div className="flex gap-1">
            <div className="animate-pulse bg-gray-200 w-6 h-6 rounded"></div>
            <div className="animate-pulse bg-gray-200 w-6 h-6 rounded"></div>
          </div>
        </div>
      </div>

      <div className="p-4">
        <CourseCardSkeleton variant="ultra-compact" count={3} />
      </div>
    </div>
  );
};

export default CourseCardSkeleton;
